import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
    MarkerMap,
    type MarkerMapHandle,
} from "tsi-common-react/src/apps/retail/MarkerMap/index";
import { formatDistance } from "tsi-common-react/src/apps/retail/helpers";
import {
    UncontrolledPopover,
    UncontrolledPopoverRef,
} from "tsi-common-react/src/components/Popover/index";
import { ILocation, RetailStore } from "tsi-common-react/src/models/location";
import { SyncStoreID } from "tsi-common-react/src/models/nominals";
import { notEmpty } from "tsi-common-react/src/utils/functional";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { c, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import fsMarker from "../../../assets/img/retail-locator/fs-marker.png";
import { FlagshipNearestStoresTile } from "../FlagshipNearestStoresTile";
import { LocatorSearch } from "../LocatorSearch";
import { LOCATION_DIALOG_TITLE_ID } from "../constants";
import { CMSFlagshipStorePage } from "../models";

const DEFAULT_MAP_CENTER = {
    lat: 37.0902,
    lng: -95.7129,
};
const DEFAULT_ZOOM = 4;

const MAP_STYLES = [
    {
        featureType: "all",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ visibility: "simplified" }, { color: "#58585b" }],
    },
    {
        featureType: "landscape",
        elementType: "all",
        stylers: [{ visibility: "simplified" }, { color: "#f2f2f2" }],
    },
    {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "administrative.country",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "administrative.locality",
        stylers: [{ visibility: "off" }],
    },
];

const getFormattedDistInMiles = (stores: Immutable<RetailStore[]>): string => {
    if (stores.length <= 0 || !stores[0].distance) {
        return t`Unknown`;
    }
    const formattedDist = formatDistance(stores[0].distance);
    return interpolate(c("FlagshipNearestStores").t`%s miles.`, [
        formattedDist,
    ]);
};

const findCMSStore = (
    externalID: SyncStoreID,
    storePages: CMSFlagshipStorePage[],
): CMSFlagshipStorePage | null => {
    const cmsStore = storePages.find((storePage) => {
        return storePage.meta.sync_id === externalID;
    });
    return cmsStore || null;
};

const InfoWindowContent = ({
    store,
    linkStoreDetails,
}: {
    store: Immutable<RetailStore>;
    linkStoreDetails: boolean;
}): React.ReactElement => {
    const url = `/locations/states/${store.state}/cities/${store.city}/stores/${store.id}/`;
    const label = interpolate(c("city, state/province").t`%s, %s`, [
        store.city,
        store.state,
    ]);
    return (
        <div className="loc__info-window">
            {linkStoreDetails ? (
                <a href={url}>{label}</a>
            ) : (
                <span>{label}</span>
            )}
        </div>
    );
};

const createInfoWindowContent = (
    store: Immutable<RetailStore>,
    linkStoreDetails: boolean,
): string => {
    return renderToStaticMarkup(
        <InfoWindowContent store={store} linkStoreDetails={linkStoreDetails} />,
    );
};

export const FlagshipNearestStores = (props: {
    stores: Immutable<RetailStore[]>;
    storePages: CMSFlagshipStorePage[];
    maxTiles: number;
    preferredLocation: ILocation | null;
    includeLocationInfo: boolean;
    onAfterClose?: () => void;
    onAfterOpen?: () => void;
}): React.ReactNode => {
    const [focusedTileID, setFocusedTileID] =
        React.useState<SyncStoreID | null>(null);
    const mapElem = React.useRef<MarkerMapHandle>(null);
    const popoverElem = React.useRef<UncontrolledPopoverRef>(null);
    const linkStoreDetailsInInfoWindow =
        usePageSetting("retail_locator_show_store_info_links") ?? true;

    const onBeforeInfoWindowOpen = (
        info: google.maps.InfoWindow,
        store: Immutable<RetailStore>,
    ) => {
        const content = createInfoWindowContent(
            store,
            linkStoreDetailsInInfoWindow,
        );
        info.setContent(content);
        setFocusedTileID(store.external_id);
    };

    const onInfoWindowClose = () => {
        setFocusedTileID(null);
    };

    const locationLabel =
        props.preferredLocation?.formatted_address_long ||
        props.preferredLocation?.formatted_address ||
        t`you`;
    const distInMiles = getFormattedDistInMiles(props.stores);
    const modalClasses = classNames({
        "filters__dropdown filters__dropdown--location": true,
        "filters__dropdown--location-sm": true,
    });

    const storesIDs: Set<SyncStoreID> = new Set();
    const tiles = props.stores
        // safety dedupe
        .reduce<Immutable<RetailStore>[]>((memo, s) => {
            if (!storesIDs.has(s.external_id)) {
                storesIDs.add(s.external_id);
                memo.push(s);
            }
            return memo;
        }, [])
        .map((store) => {
            const storePage = findCMSStore(store.external_id, props.storePages);
            if (!storePage) {
                return null;
            }
            return (
                <FlagshipNearestStoresTile
                    key={`${store.external_id}`}
                    {...props}
                    store={store}
                    cmsProps={storePage}
                    isFocused={store.external_id === focusedTileID}
                    includeLocationInfo={props.includeLocationInfo}
                    onActivateMarker={() => {
                        mapElem.current?.onActivateMarker(store.external_id);
                    }}
                />
            );
        })
        .filter(notEmpty)
        .slice(0, props.maxTiles);

    return (
        <>
            <section className="filters">
                <p className="filters__filters filters__filters--small">
                    {richT(
                        t`<strong>Closest</strong> Tempur-Pedic<sup>®</sup> Store to <location /> is <distance />`,
                        {
                            location: () => (
                                <UncontrolledPopover
                                    ref={popoverElem}
                                    triggerContent={
                                        <>
                                            <span className="ada-screenreader-only">
                                                {t`Change your location to find retailers in that area.`}
                                            </span>
                                            {locationLabel}
                                        </>
                                    }
                                    triggerBtnProps={{
                                        className:
                                            "filters__filter origin-location al-store-locator__origin-location",
                                    }}
                                    modalProps={{
                                        aria: {
                                            modal: true,
                                            labelledby:
                                                LOCATION_DIALOG_TITLE_ID,
                                        },
                                    }}
                                >
                                    <div className={modalClasses}>
                                        <LocatorSearch
                                            onSubmit={() => {
                                                if (popoverElem.current) {
                                                    popoverElem.current.requestClose();
                                                }
                                            }}
                                        />
                                    </div>
                                </UncontrolledPopover>
                            ),
                            distance: () => (
                                <strong className="bh-sl-nearest-results">
                                    {distInMiles}
                                </strong>
                            ),
                        },
                    )}
                </p>
            </section>
            <MarkerMap
                ref={mapElem}
                stores={props.stores}
                markerOptions={() => {
                    return {
                        icon: fsMarker,
                    };
                }}
                elemProps={{
                    "id": "locations__map",
                    "className": "locations__map",
                    "role": "region",
                    "aria-label": t`map`,
                }}
                mapOptions={() => {
                    return {
                        center: DEFAULT_MAP_CENTER,
                        zoom: DEFAULT_ZOOM,
                        scrollwheel: false,
                        draggable: true,
                        disableDefaultUI: false,
                        clickableIcons: false,
                        mapTypeId: google.maps.MapTypeId.ROADMAP,
                        styles: MAP_STYLES,
                    };
                }}
                onBeforeInfoWindowOpen={onBeforeInfoWindowOpen}
                onInfoWindowClose={onInfoWindowClose}
            />
            <section
                className="locations__list-wrapper l-capped-width"
                role="region"
                aria-label={t`Locations`}
            >
                <ul className="locations__list locations__list--index list">
                    {tiles}
                </ul>
            </section>
        </>
    );
};
